import { newDeck, shuffle, randInt, cardsText, fmt } from "./cards";
import { evaluate7, handName } from "./evaluator";
import { createModel, modelStartHand, modelAction, modelStreet, modelShowdown, modelEndHand } from "./opponentModel";

const MULTS = [1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80, 100, 120, 150, 200];
const NAMES = ["Maverick", "Raven", "Duke", "Sasha", "Viper", "Nova", "Rook", "Jinx"];
const SKILLS = ["novice", "regular", "pro"];
export const SKILL_LABEL = { novice: "Novice", regular: "Regular", pro: "Pro", proplus: "Pro+", proplusplus: "Pro++" };
const OPEN_RANGE = { EP: 0.15, MP: 0.2, CO: 0.28, BTN: 0.45, SB: 0.42, BB: 0.3 };
export const STREET_LABEL = { preflop: "Pre-flop", flop: "Flop", turn: "Turn", river: "River" };

const vb = (p, you, other) => `${p.name} ${p.isHuman ? you : other}`;
const sum = (a) => a.reduce((x, y) => x + y, 0);
const nice = (v) => (v < 100 ? Math.round(v / 10) * 10 : v < 1000 ? Math.round(v / 50) * 50 : Math.round(v / 100) * 100);
export const ordinal = (n) => n + (["th", "st", "nd", "rd"][((n % 100) - 20) % 10] || ["th", "st", "nd", "rd"][n % 100] || "th");

export function blindsFor(cfg, level) {
  const extra = level >= MULTS.length ? 2 ** (level - MULTS.length + 1) : 1;
  const m = MULTS[Math.min(level, MULTS.length - 1)] * extra;
  const bb = level === 0 ? cfg.bigBlind : Math.max(cfg.bigBlind, nice(cfg.bigBlind * m));
  return { sb: Math.max(1, Math.floor(bb / 2)), bb };
}

export const potTotal = (s) => sum(s.players.map((p) => p.totalBet));
const live = (s) => s.players.filter((p) => !p.out && !p.folded);
const ableToAct = (s) => live(s).filter((p) => !p.allIn);

function log(s, text, kind = "action") {
  s.logSeq += 1;
  s.log.push({ id: s.logSeq, hand: s.handNum, text, kind });
  if (s.log.length > 250) s.log.shift();
}

function nextAlive(s, i) {
  const n = s.players.length;
  let j = i;
  do { j = (j + 1) % n; } while (s.players[j].out);
  return j;
}

function commit(p, a) {
  p.stack -= a; p.bet += a; p.totalBet += a;
  if (p.stack === 0) p.allIn = true;
}

export function createTournament(cfg) {
  const n = cfg.numPlayers;
  const players = Array.from({ length: n }, (_, i) => ({
    id: i, name: i === 0 ? "You" : NAMES[i - 1], isHuman: i === 0,
    skill: i === 0 ? null : cfg.aiSkill === "mixed" ? SKILLS[randInt(3)] : cfg.aiSkill,
    stack: cfg.startingStack, startStack: cfg.startingStack, bet: 0, totalBet: 0, cards: [],
    folded: false, allIn: false, out: false, acted: false, lastAction: null, place: null,
    revealed: false, handName: null, score: 0,
  }));
  return {
    cfg, players, dealer: randInt(n), sbIdx: -1, bbIdx: -1, handNum: 0, level: 0, sb: 0, bb: 0,
    deck: [], board: [], street: "preflop", toAct: -1, currentBet: 0, minRaise: 0, raiseCount: 0,
    phase: "betting", log: [], logSeq: 0, result: null, gameOver: false, bustInfo: null,
    stats: players.map(() => ({ jams: 0, dealt: 0, vpip: 0, pfr: 0, aggr: 0, calls: 0, won: 0, showdowns: 0, sdWon: 0, biggestPot: 0 })),
    model: createModel(n), hf: [], chipHistory: [{ hand: 0, stack: cfg.startingStack }],
  };
}

function post(s, idx, amt, label) {
  const p = s.players[idx];
  const a = Math.min(amt, p.stack);
  commit(p, a);
  log(s, `${vb(p, "post", "posts")} ${label} ${fmt(a)}`);
}

function findNext(s, from) {
  const n = s.players.length;
  const able = ableToAct(s).length;
  for (let k = 1; k <= n; k++) {
    const j = (from + k) % n;
    const p = s.players[j];
    if (p.out || p.folded || p.allIn) continue;
    if (p.bet < s.currentBet) return j;
    if (!p.acted && able > 1) return j;
  }
  return -1;
}

export function startHand(s) {
  s.handNum += 1;
  const prevLevel = s.level;
  s.level = Math.floor((s.handNum - 1) / s.cfg.handsPerLevel);
  const { sb, bb } = blindsFor(s.cfg, s.level);
  s.sb = sb; s.bb = bb;
  s.levelUp = s.handNum > 1 && s.level !== prevLevel;
  if (s.handNum > 1 || s.players[s.dealer].out) s.dealer = nextAlive(s, s.dealer);
  s.players.forEach((p) => Object.assign(p, {
    bet: 0, totalBet: 0, cards: [], folded: p.out, allIn: false, acted: false, lastAction: null,
    startStack: p.stack, revealed: false, handName: null, score: 0,
  }));
  s.hf = s.players.map(() => ({ vpip: false, pfr: false, range: 1 }));
  s.pfAggressor = -1; s.lastRaiser = -1;
  const alive = s.players.filter((p) => !p.out);
  if (alive.length === 2) { s.sbIdx = s.dealer; s.bbIdx = nextAlive(s, s.dealer); }
  else { s.sbIdx = nextAlive(s, s.dealer); s.bbIdx = nextAlive(s, s.sbIdx); }
  modelStartHand(s.model, Object.fromEntries(alive.map((p) => [p.id, positionOf(s, p.id)])));
  s.deck = shuffle(newDeck());
  s.board = []; s.street = "preflop"; s.result = null; s.phase = "betting";
  log(s, `Hand #${s.handNum} · Blinds ${fmt(sb)}/${fmt(bb)}`, "hand");
  post(s, s.sbIdx, sb, "small blind");
  post(s, s.bbIdx, bb, "big blind");
  const bets = s.players.map((p) => p.bet);
  s.currentBet = s.players[s.bbIdx].allIn && alive.length === 2 ? Math.max(...bets) : Math.max(bb, ...bets);
  s.minRaise = bb; s.raiseCount = 0;
  for (let r = 0; r < 2; r++) {
    let j = s.sbIdx;
    for (let k = 0; k < alive.length; k++) { s.players[j].cards.push(s.deck.pop()); j = nextAlive(s, j); }
  }
  log(s, `You are dealt ${cardsText(s.players[0].cards)}`, s.players[0].out ? "hidden" : "deal");
  s.toAct = findNext(s, s.bbIdx);
  if (s.toAct === -1) closeBetting(s);
}

export function applyAction(s, idx, a) {
  const p = s.players[idx];
  const toCall = Math.max(0, s.currentBet - p.bet);
  let type = a.type;
  if (type === "raise" && (a.amount <= s.currentBet || p.stack <= toCall)) type = "call";
  if (type === "check" && toCall > 0) type = "fold";
  if (type === "call" && toCall === 0) type = "check";
  const pre = s.street === "preflop";
  const st = s.stats[idx], hf = s.hf[idx];
  const raiseTarget = Math.min(Math.max(Math.round(a.amount), s.currentBet + s.minRaise), p.stack + p.bet);
  modelAction(s.model, {
    id: idx, pos: positionOf(s, idx), street: s.street, type, toCall, potBefore: potTotal(s), rc: s.raiseCount,
    limpers: pre && s.raiseCount === 0 ? s.players.filter((q) => q.id !== s.bbIdx && q.lastAction === "Call").length : 0,
    nLive: live(s).length, allIn: type === "raise" ? raiseTarget >= p.stack + p.bet : type === "call" && toCall >= p.stack,
  });
  if (pre && (type === "call" || type === "raise")) hf.range = Math.min(hf.range, assumedRange(s, idx, type, toCall, a.amount));
  if (!pre && type === "raise") hf.range = Math.max(0.03, hf.range * 0.8);
  if (!pre && type === "call") hf.range = Math.max(0.03, hf.range * 0.92);
  if (type === "fold") {
    p.folded = true; p.lastAction = "Fold"; log(s, vb(p, "fold", "folds"));
  } else if (type === "check") {
    p.lastAction = "Check"; log(s, vb(p, "check", "checks"));
  } else if (type === "call") {
    const amt = Math.min(toCall, p.stack);
    commit(p, amt);
    p.lastAction = p.allIn ? "All-in" : "Call";
    log(s, `${vb(p, "call", "calls")} ${fmt(amt)}${p.allIn ? " (all-in)" : ""}`);
    if (pre) hf.vpip = true; else st.calls++;
  } else {
    const to = Math.min(Math.max(Math.round(a.amount), s.currentBet + s.minRaise), p.stack + p.bet);
    const wasBet = s.currentBet === 0;
    const inc = to - s.currentBet;
    commit(p, to - p.bet);
    if (inc >= s.minRaise) {
      s.minRaise = inc;
      s.players.forEach((q, i) => { if (i !== idx) q.acted = false; });
    }
    s.currentBet = to; s.raiseCount++; s.lastRaiser = idx;
    if (pre) s.pfAggressor = idx;
    p.lastAction = p.allIn ? "All-in" : wasBet ? "Bet" : "Raise";
    log(s, `${wasBet ? vb(p, "bet", "bets") : vb(p, "raise to", "raises to")} ${fmt(to)}${p.allIn ? " (all-in)" : ""}`);
    if (pre) { hf.vpip = true; hf.pfr = true; } else st.aggr++;
  }
  p.acted = true;
  const l = live(s);
  if (l.length === 1) return finishUncontested(s, l[0]);
  const nxt = findNext(s, idx);
  if (nxt === -1) closeBetting(s); else s.toAct = nxt;
}

// Public read of a player's likely pre-flop range (top fraction of hands) from the actions everyone saw
function assumedRange(s, idx, type, toCall, amount) {
  const rc = s.raiseCount;
  const pos = positionOf(s, idx);
  const hu = s.players.filter((q) => !q.out).length === 2;
  const p = s.players[idx];
  if (type === "call") return rc === 0 ? (pos === "SB" ? 0.6 : 0.5) : rc === 1 ? (pos === "BB" ? 0.55 : 0.3) : 0.1;
  const jam = type === "raise" && (amount >= p.stack + p.bet || p.stack <= toCall + s.bb * 12);
  if (jam) {
    const st = s.stats[idx];
    st.jams++;
    const freq = Math.min(1, ((st.jams + 1) / (st.dealt + 3)) * 1.15);
    return Math.max(rc === 0 ? (hu ? 0.45 : 0.3) : rc === 1 ? 0.1 : 0.04, freq);
  }
  if (rc === 0) return hu ? 0.8 : OPEN_RANGE[pos];
  return rc === 1 ? (hu ? 0.18 : 0.1) : 0.04;
}

function closeBetting(s) {
  s.players.forEach((p) => {
    p.bet = 0; p.acted = false;
    if (!p.folded && p.lastAction !== "All-in") p.lastAction = null;
  });
  s.currentBet = 0; s.minRaise = s.bb; s.raiseCount = 0; s.toAct = -1; s.lastRaiser = -1;
  if (s.street === "river") return showdown(s);
  if (ableToAct(s).length <= 1) {
    live(s).forEach((p) => { p.revealed = true; });
    s.phase = "runout";
    return;
  }
  dealNext(s);
}

function dealNext(s) {
  s.deck.pop();
  if (s.street === "preflop") { s.board.push(s.deck.pop(), s.deck.pop(), s.deck.pop()); s.street = "flop"; }
  else { s.board.push(s.deck.pop()); s.street = s.street === "flop" ? "turn" : "river"; }
  modelStreet(s.model, s.street, live(s).map((p) => p.id));
  log(s, `${STREET_LABEL[s.street]}: ${cardsText(s.board)}`, "street");
  if (s.phase !== "runout") s.toAct = findNext(s, s.dealer);
}

export function advanceRunout(s) {
  if (s.street === "river") showdown(s); else dealNext(s);
}

function refund(s) {
  const sorted = [...s.players].sort((a, b) => b.totalBet - a.totalBet);
  const diff = sorted[0].totalBet - (sorted[1]?.totalBet || 0);
  if (diff > 0) {
    sorted[0].stack += diff; sorted[0].totalBet -= diff;
    log(s, `Uncalled ${fmt(diff)} returned to ${sorted[0].name}`);
  }
}

function buildPots(s) {
  const l = live(s);
  const levels = [...new Set(l.map((p) => p.totalBet))].sort((a, b) => a - b);
  const pots = [];
  let prev = 0;
  levels.forEach((L) => {
    const amount = sum(s.players.map((p) => Math.min(p.totalBet, L) - Math.min(p.totalBet, prev)));
    const eligible = l.filter((p) => p.totalBet >= L).map((p) => p.id);
    if (amount > 0) pots.push({ amount, eligible });
    prev = L;
  });
  const leftover = potTotal(s) - sum(pots.map((p) => p.amount));
  if (leftover > 0 && pots.length) pots[pots.length - 1].amount += leftover;
  return pots;
}

function finishUncontested(s, w) {
  refund(s);
  const amt = potTotal(s);
  w.stack += amt;
  s.result = { showdown: false, pots: [{ amount: amt, winners: [w.id], handName: null }] };
  log(s, `${vb(w, "win", "wins")} ${fmt(amt)}`, "win");
  finalize(s);
}

function showdown(s) {
  refund(s);
  const l = live(s);
  l.forEach((p) => {
    p.revealed = true;
    p.score = evaluate7([...p.cards, ...s.board]);
    p.handName = handName(p.score);
    s.stats[p.id].showdowns++;
    log(s, `${vb(p, "show", "shows")} ${cardsText(p.cards)} — ${p.handName}`, "show");
  });
  const order = [];
  for (let k = 1, j = s.dealer; k <= s.players.length; k++) { j = (j + 1) % s.players.length; order.push(j); }
  const results = buildPots(s).map((pot, i, all) => {
    const best = Math.max(...pot.eligible.map((id) => s.players[id].score));
    const ws = order.filter((id) => pot.eligible.includes(id) && s.players[id].score === best);
    const share = Math.floor(pot.amount / ws.length);
    let rem = pot.amount - share * ws.length;
    ws.forEach((id) => { s.players[id].stack += share + (rem > 0 ? 1 : 0); if (rem > 0) rem--; });
    const label = all.length > 1 ? (i === 0 ? "main pot" : `side pot ${i}`) : "the pot";
    log(s, `${ws.map((id) => s.players[id].name).join(" & ")} ${ws.length > 1 ? "split" : ws[0] === 0 ? "win" : "wins"} ${label} ${fmt(pot.amount)} with ${s.players[ws[0]].handName}`, "win");
    return { amount: pot.amount, winners: ws, handName: s.players[ws[0]].handName };
  });
  const winnerIds = new Set(results.flatMap((r) => r.winners));
  winnerIds.forEach((id) => s.stats[id].sdWon++);
  modelShowdown(s.model, l.map((p) => ({ id: p.id, score: p.score, won: winnerIds.has(p.id) })));
  s.result = { showdown: true, pots: results };
  finalize(s);
}

function finalize(s) {
  modelEndHand(s.model);
  s.players.forEach((p) => { p.bet = 0; });
  const won = {};
  s.result.pots.forEach((r) => r.winners.forEach((id) => { won[id] = (won[id] || 0) + r.amount / r.winners.length; }));
  Object.entries(won).forEach(([id, amt]) => {
    const st = s.stats[id];
    st.won++; st.biggestPot = Math.max(st.biggestPot, Math.round(amt));
  });
  s.players.forEach((p) => {
    if (p.out) return;
    const st = s.stats[p.id];
    st.dealt++; if (s.hf[p.id].vpip) st.vpip++; if (s.hf[p.id].pfr) st.pfr++;
  });
  const busted = s.players.filter((p) => !p.out && p.stack === 0).sort((a, b) => a.startStack - b.startStack);
  let alive = s.players.filter((p) => !p.out).length;
  const lastPot = s.result.pots[s.result.pots.length - 1];
  busted.forEach((p) => {
    p.out = true; p.place = alive--;
    log(s, `${vb(p, "are", "is")} eliminated in ${ordinal(p.place)} place`, "bust");
    if (p.isHuman) {
      const top = s.result.pots[0];
      s.bustInfo = { hand: s.handNum, by: top.winners.map((id) => s.players[id].name).join(" & "), handName: top.handName || lastPot.handName, cards: cardsText(p.cards), board: cardsText(s.board) };
    }
  });
  const remaining = s.players.filter((p) => !p.out);
  if (remaining.length === 1) { remaining[0].place = 1; s.gameOver = true; }
  if (s.players[0].out) s.gameOver = true;
  s.chipHistory.push({ hand: s.handNum, stack: s.players[0].stack });
  s.phase = "handover"; s.toAct = -1;
}

export function positionOf(s, idx) {
  if (idx === s.sbIdx) return "SB";
  if (idx === s.bbIdx) return "BB";
  if (idx === s.dealer) return "BTN";
  const order = [];
  let j = s.bbIdx;
  while (true) { j = nextAlive(s, j); if (j === s.dealer) break; order.push(j); }
  const i = order.indexOf(idx);
  if (i === order.length - 1) return "CO";
  return i < Math.ceil((order.length - 1) / 2) ? "EP" : "MP";
}

// Public information + the player's OWN hole cards only. This is all an AI ever sees.
export function viewFor(s, idx) {
  const p = s.players[idx];
  const l = live(s);
  const toCall = Math.max(0, Math.min(s.currentBet - p.bet, p.stack));
  const maxTo = p.stack + p.bet;
  const othersAble = l.some((q) => q.id !== idx && !q.allIn);
  const minRaiseTo = Math.min(maxTo, Math.max(s.currentBet + s.minRaise, s.bb));
  const n = s.players.length;
  let inPosition = true;
  for (let k = 1, j = idx; k < n; k++) {
    j = (j + 1) % n;
    if (j === (s.dealer + 1) % n) break;
    const q = s.players[j];
    if (!q.out && !q.folded && !q.allIn) { inPosition = false; break; }
  }
  if (idx === s.dealer) inPosition = true;
  const maxOpp = Math.max(0, ...l.filter((q) => q.id !== idx).map((q) => q.stack + q.bet));
  const limpers = s.street === "preflop" && s.raiseCount === 0
    ? s.players.filter((q) => q.id !== s.bbIdx && q.lastAction === "Call").length : 0;
  return {
    hole: [...p.cards], board: [...s.board], pot: potTotal(s), toCall, currentBet: s.currentBet,
    minRaiseTo, maxTo, bet: p.bet, stack: p.stack, bb: s.bb, sbUnit: Math.max(1, s.sb),
    pos: positionOf(s, idx), nOpp: l.length - 1, inPosition, street: s.street, raiseCount: s.raiseCount,
    canRaise: !p.acted && p.stack > toCall && othersAble, limpers, effBB: Math.min(maxTo, maxOpp) / s.bb,
    tableSize: s.players.filter((q) => !q.out).length,
    wasAggressor: s.pfAggressor === idx,
    model: s.model, raiserId: s.lastRaiser,
    opponents: l.filter((q) => q.id !== idx).map((q) => ({ id: q.id, pos: positionOf(s, q.id), range: s.hf[q.id].range, allIn: q.allIn, stackBB: (q.stack + q.bet) / s.bb })),
    raiserRange: s.lastRaiser >= 0 ? s.hf[s.lastRaiser].range : null,
    raiserPos: s.lastRaiser >= 0 ? positionOf(s, s.lastRaiser) : null,
  };
}
